<template>
  <div class="page-shell">
    <div v-if="isLoading" class="fetching-state">
      <LoadingState
        :loading="isLoading"
        message="Loading quotation details..."
        :spinner-size="24"
        :spinner-width="3"
      />
    </div>

    <p v-else-if="errorMessage" class="error-text">{{ errorMessage }}</p>

    <div v-else-if="!quotationData" class="info-note">
      <v-icon size="18" class="info-note-icon">mdi-information-outline</v-icon>
      <p class="info-note-text">Quotation details are unavailable.</p>
    </div>

    <template v-else>
      <AppCard class="vendor-card">
        <div class="vendor-icon">
          <v-icon size="18">mdi-account-outline</v-icon>
        </div>
        <div class="vendor-copy">
          <div class="vendor-name-row">
            <div>
              <p class="vendor-name">{{ clientInfo.name }}</p>
              <div class="vendor-rating" v-if="clientInfo.phone">
                <v-icon size="14" class="meta-icon">mdi-phone-outline</v-icon>
                <span>{{ clientInfo.phone }}</span>
              </div>
              <div class="vendor-rating" v-if="clientInfo.email">
                <v-icon size="14" class="meta-icon">mdi-email-outline</v-icon>
                <span>{{ clientInfo.email }}</span>
              </div>
            </div>
            <div>
              <v-chip
                label
                size="small"
                class="text-capitalize"
                :color="getStatusColor(quotationStatus)"
              >
                {{ quotationStatus }}
              </v-chip>
            </div>
          </div>
        </div>
      </AppCard>

      <AppCard class="amount-card">
        <p class="amount-title">Quoted Amount</p>
        <p class="amount-value">{{ formatCurrency(totalSummary.totalAmount) }}</p>
      </AppCard>

      <!-- <AppCard class="message-card">
        <AppSectionHeader title="Provider Message" />
        <v-divider></v-divider>
        <p class="message-body">
          We can fix your AC within the same day. Includes refrigerant refill and
          filter cleaning.
        </p>
      </AppCard> -->

      <AppCard class="detail-card">
        <AppSectionHeader title="Quotation Detail" />
        <v-divider></v-divider>

        <div v-for="section in quoteSections" :key="section.title" class="quote-section">
          <p class="section-name">{{ section.title }}</p>
          <div class="quote-table-shell">
            <table class="quote-table">
              <thead>
                <tr>
                  <th class="quote-col-item">Item</th>
                  <th class="quote-col-qty">Qty</th>
                  <th class="quote-col-price">Price</th>
                  <th class="quote-col-total">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in section.items" :key="`${section.title}-${item.name}`">
                  <td class="quote-col-item">{{ item.name }}</td>
                  <td class="quote-col-qty">{{ item.quantity }}</td>
                  <td class="quote-col-price">
                    {{ formatCurrency(item.price) }}
                  </td>
                  <td class="quote-col-total">
                    {{ formatCurrency(item.total) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="section-total-label" colspan="3">Section Total</td>
                  <td class="section-total">
                    {{ formatCurrency(section.total) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

      </AppCard>
        <table class="quote-summary">
          <tbody>
            <tr class="summary-row">
              <td >Sub Total</td>
              <td class="summary-value">{{ formatCurrency(totalSummary.subTotal) }}</td>
            </tr>
            <tr class="summary-row">
              <td>Tax</td>
              <td class="summary-value">{{ formatCurrency(totalSummary.taxAmount) }}</td>
            </tr>
            <tr class="summary-row">
              <td>Online Fee</td>
              <td class="summary-value">{{ formatCurrency(totalSummary.onlineFee) }}</td>
            </tr>
            <tr class="summary-row">
              <td>Total</td>
              <td class="summary-value">{{ formatCurrency(totalSummary.totalAmount) }}</td>
            </tr>
          </tbody>
        </table>

      <div class="text-right">
        <p class="detail-label">Quoted On</p>
        <p class="detail-value">{{ quotedOn }}</p>
      </div>
    </template>
  </div>
  <QuotationConvertAction
    v-if="quotationStatus == 'accepted'"
    :quotation-id="quotationId"
    :on-success="loadQuotationDetail"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import { fetchVendorQuotationDetail, type QuotationDetailResponse } from "@/shared/services/quotations.api";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppSectionHeader from "@/shared/components/ui/AppSectionHeader.vue";
import { formatCurrency, getStatusColor } from "@/shared/utils/utils";
import QuotationConvertAction from "@/modules/vendor/components/quotation-detail/QuotationConvertAction.vue";

type QuoteTotals = {
  sub_total: number;
  tax_amount: number;
  online_fee: number;
  total_amount: number;
};
type QuoteSectionItem = {
  name: string;
  quantity: number;
  price: number;
  total: number;
};
type QuoteSection = {
  title: string;
  items: QuoteSectionItem[];
  total: number;
};
const quotedOn = computed(() =>
  quotationData.value?.created_at ?? "—"
);

const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref("");
const quotationData = ref<QuotationDetailResponse | null>(null);

const quotationId = computed(() => String(route.params.id ?? quotationData.value?.id ?? "").trim());

function toNumber(value: unknown, fallback = 0) {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  const parsed = Number.parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

const quoteSections = computed<QuoteSection[]>(() => {
  const sections = quotationData.value?.sections ?? [];
  return sections.map((section, sectionIndex) => {
    const items: QuoteSectionItem[] = (section.items ?? []).map((item, itemIndex) => {
      const quantity = Math.max(toNumber(item.qty ?? 1, 1), 1);
      const price = toNumber(item.price ?? 0, 0);
      const total = toNumber(item.line_total ?? 0, quantity * price);
      return {
        name: String(item.title ?? `Item ${itemIndex + 1}`),
        quantity,
        price,
        total,
      };
    });

    return {
      title: String(section.title ?? `Section ${sectionIndex + 1}`),
      items,
      total: items.reduce((sum, item) => sum + item.total, 0),
    };
  });
});

const totalInfo = computed<QuoteTotals>(() => {
  const info = quotationData.value?.total_info ?? null;
  return {
    sub_total: toNumber(info?.sub_total, 0),
    tax_amount: toNumber(info?.tax_amount, 0),
    online_fee: toNumber((info as any)?.online_fee, 0),
    total_amount: toNumber(info?.total_amount, 0),
  };
});

const totalSummary = computed(() => ({
  subTotal: totalInfo.value.sub_total,
  taxAmount: totalInfo.value.tax_amount,
  onlineFee: totalInfo.value.online_fee,
  totalAmount: totalInfo.value.total_amount,
}));

const clientRecord = computed(() => {
  return quotationData.value?.client ?? null;
});

const clientInfo = computed(() => ({
  name: clientRecord.value?.name ?? "Client",
  email: clientRecord.value?.email ?? "",
  phone: clientRecord.value?.phone_no ?? "",
}));

const quotationStatus = computed(() =>
  quotationData.value?.status ?? "new"
);

async function loadQuotationDetail() {
  const id = String(route.params.id ?? "").trim();
  if (!id) {
    quotationData.value = null;
    errorMessage.value = "Quotation not found.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response = await fetchVendorQuotationDetail(id);
    quotationData.value = response ?? null;
    if (!quotationData.value) errorMessage.value = "Quotation details are unavailable.";
  } catch (error) {
    quotationData.value = null;
    errorMessage.value = "Unable to load quotation details.";
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => route.params.id,
  () => {
    loadQuotationDetail();
  },
);

onMounted(loadQuotationDetail);
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px;
}

.fetching-state {
  padding: 12px 0;
}

.error-text {
  margin: 0;
  font-size: 14px;
  color: #b91c1c;
  font-weight: 600;
}

.info-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #f8fbff;
  color: #475569;
}

.info-note-icon {
  color: #2563eb;
  flex-shrink: 0;
}

.info-note-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

.vendor-card {
  background: #fff;
  display: flex;
  align-items: center;
  gap: 14px;
}

.vendor-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #fff2e9;
  color: #f97316;
  overflow: hidden;
  flex-shrink: 0;
}

.vendor-copy {
  min-width: 0;
  flex: 1;
}

.vendor-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vendor-name {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.quote-status-chip {
  text-transform: capitalize;
}

.vendor-rating {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-default);
  color: #6b7280;
  font-weight: 600;
}

.meta-icon {
  color: #6b7280;
}

.amount-card {
  padding: 16px;
  text-align: center;
}

.amount-title {
  margin: 0;
  font-size: var(--font-size-default);
  color: #6b7280;
  font-weight: 600;
}

.amount-value {
  margin: 6px 0 0;
  font-size: 22px;
  color: #f97316;
  font-weight: 700;
}

.message-card {
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.message-head {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #111827;
}

.message-title {
  margin: 0;
  font-size: var(--font-size-default);
}

.message-body {
  margin: 0;
  font-size: var(--font-size-default);
  color: #6b7280;
  line-height: 1.5;
}

.detail-card {
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-card-head {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #111827;
}

.detail-card-title {
  margin: 0;
  font-size: var(--font-size-default);
}

.quote-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-name {
  margin: 2px 0 0;
  font-size: 13px;
  font-weight: 500;
}

.quote-table-shell {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.quote-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.quote-table th,
.quote-table td {
  padding: 8px 7px;
  text-align: left;
}

.quote-table th {
  background: #f8fafc;
  color: #475569;
  font-size: var(--font-size-sm);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.quote-table td {
  color: #111827;
  font-size: 14px;
  border-bottom: 1px solid #eef2f6;
}

.quote-table tbody tr:last-child td {
  border-bottom: 1px solid #e2e8f0;
}

.quote-table tfoot td {
  padding-top: 9px;
  padding-bottom: 9px;
  border-bottom: 0;
  background: #f8fafc;
  font-size: var(--font-size-default);
}

.quote-col-item {
  width: 46%;
}

.quote-col-qty {
  width: 12%;
  text-align: center;
}

.quote-col-price,
.quote-col-total {
  width: 21%;
  text-align: right;
}

.section-total {
  font-weight: 700;
  color: #111827;
  text-align: right;
}

.section-total-label {
  font-weight: 600;
  color: #64748b;
  text-align: right;
}

.quote-summary {
  margin-top: 4px;
  margin-left: auto;
  width: min(360px, 100%);
  font-size: 14px;
  border-collapse: collapse;
  border: none;
}

.summary-row {
  font-weight: 600;
}

.summary-row td {
  padding-top: 8px;
  text-align: right;
}

.summary-value {
  text-align: right;
}

.details-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #fff2e9;
  color: #f97316;
}

.detail-label {
  margin: 0;
  font-size: var(--font-size-default);
  color: var(--color-subtitle-light);
  font-weight: 500;
}

.detail-value {
  margin: 2px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-title);
}
</style>
